import { useTranslation } from '@mint-lib/i18n';
import { Download } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import { Button, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { AutoImportErrorAlert } from '../../components/molecules/AutoImportErrorAlert.jsx';
import { NavbarSlot } from '../../components/molecules/NavbarSlot.jsx';
import { SheetSelectionDialog } from '../../components/molecules/SheetSelectionDialog.jsx';
import { TemplateMissmatchAlertDialog } from '../../components/molecules/TemplateMissmatchAlertDialog.jsx';
import { useDataFileContext } from '../../contexts/DataFileContext/useDataFileContext.js';
import {
  useDataFilePreview,
  useDiscardTemplate,
  useExampleCsvFile,
  useIsDataFileContextFilled,
  useKeepTemplate,
  useUpdateDataFilePreview,
} from '../../queries/index.js';
import { saveFile } from '../../utils/files.js';

export const UploadStep = () => {
  const { t } = useTranslation('@myn/import-hub');
  const { dataFileId, showUploadFileDialog } = useDataFileContext();

  const {
    data: {
      templateSheetsMismatched,
      templateHeadersMismatched,
      error: autoImportError,
    },
  } = useDataFilePreview(dataFileId);

  const isContextFilled = useIsDataFileContextFilled(dataFileId);

  const { mutate: keepTemplateAndDiscardFile } = useKeepTemplate(
    dataFileId,
    true,
  );
  const { mutate: keepTemplateAndLeave } = useKeepTemplate(dataFileId, false);

  const { mutate: discardTemplate } = useDiscardTemplate(dataFileId);

  const closeAlertDialog = () => {
    keepTemplateAndLeave();
  };

  const handleKeepTemplateAndDiscardFile = () => {
    keepTemplateAndDiscardFile();
    showUploadFileDialog();
  };

  const handleDiscardTemplate = () => {
    discardTemplate();
  };

  const { mutate: updateDataFilePreview } =
    useUpdateDataFilePreview(dataFileId);

  const closeSheetSelectionDialog = () => {
    updateDataFilePreview({
      templateSheetsMismatched: false,
      isFileUploaded: false,
    });
  };

  const { data: exampleCsv } = useExampleCsvFile();

  return (
    <>
      <NavbarSlot>
        <Button
          onClick={() => saveFile(exampleCsv as BlobPart)}
          variant="text"
          data-dd-action-name="Download CSV file example"
        >
          <Download />
          {t('Download CSV file example')}
        </Button>
      </NavbarSlot>
      {autoImportError && <AutoImportErrorAlert error={autoImportError} />}
      <h4
        className={cn(
          typographyVariants({ variant: 'h4', color: 'primary' }),
          'mb-2',
        )}
      >
        {t('File import setup')}
      </h4>
      <p
        className={typographyVariants({
          variant: 'bodyLong01',
          color: 'primary',
        })}
      >
        {t('Choose a file to import and select the header row of your file.')}
      </p>
      <div className="flex flex-1 flex-col items-center justify-center">
        <Illustration
          className="size-38.5"
          component="IllustrationFileUpload"
        />
        <h4
          className={cn(
            typographyVariants({ variant: 'h4', color: 'primary' }),
            'my-4',
          )}
        >
          {t('Upload a file to start')}
        </h4>
        <Button
          onClick={showUploadFileDialog}
          disabled={!isContextFilled}
          size="large"
          data-dd-action-name="Upload"
        >
          {t('Upload')}
        </Button>
      </div>
      {templateSheetsMismatched && (
        <SheetSelectionDialog onClose={closeSheetSelectionDialog} />
      )}
      {templateHeadersMismatched && (
        <TemplateMissmatchAlertDialog
          onClose={closeAlertDialog}
          onReplaceFile={handleKeepTemplateAndDiscardFile}
          onDiscardTemplate={handleDiscardTemplate}
        />
      )}
    </>
  );
};
